import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { inventoryApi as I, INV_STATUS_STYLE, GRADE_STYLE } from "@/lib/inventory";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lock, PackageCheck, Undo2, Hand, Truck, SlidersHorizontal, MapPin } from "lucide-react";

export default function InventoryItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveForm, setReserveForm] = useState({ so_no: "", note: "" });
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ new_meters: "", reason: "" });
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveForm, setMoveForm] = useState({ warehouse: "", rack: "", bin: "" });
  const canEdit = can("inventory", "edit");
  const canApprove = can("inventory", "approve");

  const load = async () => {
    setLoading(true);
    try { const d = await I.item(id); setItem(d); setMoveForm(d.location || { warehouse: "", rack: "", bin: "" }); setAdjustForm({ new_meters: d.meters, reason: "" }); }
    catch { toast.error("Not found"); navigate("/inventory"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const act = async (fn, ok) => { try { setItem(await fn()); toast.success(ok); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const doReserve = async () => { if (!reserveForm.so_no) { toast.error("Enter SO number"); return; } await act(() => I.reserve(id, reserveForm.so_no, reserveForm.note), "Reserved"); setReserveOpen(false); };
  const doAdjust = async () => { if (!adjustForm.reason) { toast.error("Enter a reason"); return; } await act(() => I.adjust(id, Number(adjustForm.new_meters), adjustForm.reason), "Adjusted"); setAdjustOpen(false); };
  const doMove = async () => { await act(() => I.move(id, moveForm), "Location updated"); setMoveOpen(false); };

  if (loading || !item) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="inventory-item-page">
      <button onClick={() => navigate("/inventory")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to inventory</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{item.item_no}</h2>
            <Badge className={`hover:bg-inherit ${INV_STATUS_STYLE[item.status]}`} data-testid="inv-status-badge">{item.status}</Badge>
            {item.qc_grade && <Badge className={`hover:bg-inherit ${GRADE_STYLE[item.qc_grade]}`}>Grade {item.qc_grade}</Badge>}
          </div>
          <p className="mt-1 text-sm text-slate-500">{item.batch_id} · {item.customer} · {item.meters} m</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {item.status === "Available" && <Button className="bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => setReserveOpen(true)} data-testid="reserve-btn"><PackageCheck className="mr-2 h-4 w-4" />Reserve</Button>}
            {item.status === "Reserved" && <Button variant="outline" onClick={() => act(() => I.release(id), "Released")} data-testid="release-btn"><Undo2 className="mr-2 h-4 w-4" />Release</Button>}
            {item.status === "Reserved" && <Button variant="outline" onClick={() => act(() => I.pick(id), "Picked")} data-testid="pick-btn"><Hand className="mr-2 h-4 w-4" />Pick</Button>}
            {item.status === "Picked" && <Button variant="outline" onClick={() => act(() => I.dispatch(id, item.reserved_for?.so_no || ""), "Dispatched")} data-testid="dispatch-btn"><Truck className="mr-2 h-4 w-4" />Dispatch</Button>}
            {!["Dispatched", "Adjusted"].includes(item.status) && <Button variant="outline" onClick={() => setMoveOpen(true)} data-testid="move-btn"><MapPin className="mr-2 h-4 w-4" />Move</Button>}
            {canApprove && !["Dispatched", "Adjusted"].includes(item.status) && <Button variant="outline" className="border-rose-300 text-rose-700" onClick={() => setAdjustOpen(true)} data-testid="adjust-btn"><SlidersHorizontal className="mr-2 h-4 w-4" />Adjust</Button>}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-6">
          <h3 className="font-heading text-base font-semibold text-slate-900">Traceability</h3>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Roll / Package</dt><dd className="font-medium text-slate-800">{item.batch_id}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Production Job</dt><dd className="text-slate-700">{item.job_no || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Sales Order</dt><dd className="text-slate-700">{item.so_no || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">QC inspection</dt><dd className="text-slate-700">{item.qc_inspection_no || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Artwork</dt><dd className="text-right text-slate-700">{item.artwork ? `${item.artwork.code || ""} V${item.artwork.version || ""}` : "—"}</dd></div>
          </dl>
        </Card>
        <Card className="p-6">
          <h3 className="font-heading text-base font-semibold text-slate-900">Stock</h3>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Meters on hand</dt><dd className="font-medium text-slate-800">{item.meters} m</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Location</dt><dd className="text-slate-700">{[item.location?.warehouse, item.location?.rack, item.location?.bin].filter(Boolean).join(" / ") || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Reserved for</dt><dd className="text-slate-700">{item.reserved_for?.so_no || "—"}</dd></div>
          </dl>
        </Card>
        <Card className="p-6">
          <h3 className="font-heading text-base font-semibold text-slate-900">Customer</h3>
          <p className="mt-3 text-sm font-medium text-slate-800">{item.customer || "—"}</p>
          {item.so_no && <button className="mt-2 text-xs text-indigo-600 hover:underline" onClick={() => navigate("/sales-orders")}>View sales orders →</button>}
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-900"><Lock className="h-4 w-4 text-slate-400" />Immutable ledger</h3>
        <div className="mt-4 space-y-2">
          {(item.ledger || []).map((l) => (
            <div key={l.id} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm" data-testid="item-ledger-row">
              <div>
                <span className="font-medium capitalize text-slate-800">{l.type}</span> <span className="text-xs text-slate-400">{l.from_status}→{l.to_status}</span>
                {l.reason && <span className="block text-xs text-slate-500">{l.reason}</span>}
              </div>
              <div className="text-right">
                <span className={`tabular-nums ${l.meters_delta < 0 ? "text-rose-600" : l.meters_delta > 0 ? "text-emerald-600" : "text-slate-400"}`}>{l.meters_delta > 0 ? "+" : ""}{l.meters_delta} m</span>
                <span className="block text-xs text-slate-400">{l.by} · {new Date(l.at).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={reserveOpen} onOpenChange={setReserveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reserve stock</DialogTitle><DialogDescription>Reservation does not reduce stock — Dispatch deducts it later.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Sales Order no</Label><Input value={reserveForm.so_no} onChange={(e) => setReserveForm({ ...reserveForm, so_no: e.target.value })} data-testid="reserve-so-input" /></div>
            <div className="space-y-1.5"><Label>Note</Label><Input value={reserveForm.note} onChange={(e) => setReserveForm({ ...reserveForm, note: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setReserveOpen(false)}>Cancel</Button><Button onClick={doReserve} data-testid="confirm-reserve-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Reserve</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust stock (approval)</DialogTitle><DialogDescription>Corrects on-hand meters with an audited ledger entry. Set 0 to write off.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>New meters</Label><Input type="number" value={adjustForm.new_meters} onChange={(e) => setAdjustForm({ ...adjustForm, new_meters: e.target.value })} data-testid="adjust-meters-input" /></div>
            <div className="space-y-1.5"><Label>Reason</Label><Input value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} data-testid="adjust-reason-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAdjustOpen(false)}>Cancel</Button><Button onClick={doAdjust} data-testid="confirm-adjust-btn" className="bg-rose-600 text-white hover:bg-rose-700">Apply adjustment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Move location</DialogTitle><DialogDescription>Record a warehouse/rack/bin move.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5"><Label className="text-xs">Warehouse</Label><Input value={moveForm.warehouse} onChange={(e) => setMoveForm({ ...moveForm, warehouse: e.target.value })} data-testid="move-warehouse-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Rack</Label><Input value={moveForm.rack} onChange={(e) => setMoveForm({ ...moveForm, rack: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Bin</Label><Input value={moveForm.bin} onChange={(e) => setMoveForm({ ...moveForm, bin: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setMoveOpen(false)}>Cancel</Button><Button onClick={doMove} data-testid="confirm-move-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
